import React, { useEffect } from "react"
import "./career.css"

// ─── Experience data ─────────────────────────────────────────────────────────
const experiences = [
  {
    position: "Senior Application Developer",
    company: "ADP",
    period: "Jul 2022 - Present",
    description:
      "Engineered an auto-logout system saving ~$4M annually. Redesigned chatbot intent-handling in-house saving $2M+. Led callback scheduling system improving efficiency by 40%. Architected agent-side voice/chatbot interface improving client satisfaction by 60%.",
  },
  {
    position: "Software Developer Intern",
    company: "FieldWorker",
    period: "Jan 2022 - May 2022",
    description:
      "Collaborated to build REST APIs and integrate with Front End using Node.js and SQL. Created common components and API layer using React.",
  },
  {
    position: "Graduate Student Assistant",
    company: "Stevens Institute of Technology",
    period: "Jun 2021 - Jan 2022",
    description:
      "Developed Site-Wide Search using ReactJS and Elastic Search, improving search performance by 90%. Built Information System Software removing manual processes by 100%. Saved 95% of user time via excel import/export.",
  },
  {
    position: "Software Developer",
    company: "Coditas",
    period: "Nov 2018 - Dec 2020",
    description:
      "Developed 2 mobile apps and 5 full-stack applications increasing sales by 20%. Delivered pixel-perfect UIs with API integration. Improved team productivity by 60% via automation scripts.",
  },
  {
    position: "Quality Assurance",
    company: "Cybage Software",
    period: "Jul 2017 - Nov 2018",
    description:
      "Generated test plans for enhancements and bug fixes. Performed Deep linking, GDPR, Mobile, and A/B testing. Built a hotel listing app using React Native and REST API.",
  },
]

function Experience() {
  useEffect(() => {
    // The page scrolls inside #containerElement (a fixed-height overflow:scroll div),
    // NOT window. We must listen to that element's scroll event.
    //
    // Red1's GSAP ScrollTrigger config (translated to vanilla):
    //   trigger: ".career-section"
    //   start:   "top 50%"   → section top = scroller 50% down
    //   end:     "bottom 30%" → section bottom = scroller 30% down
    //   scrub:   1.5          → lerp coefficient ~0.08

    const scroller = document.getElementById("containerElement") || window

    let targetP = 0
    let currentP = 0
    let rafId = null
    let alive = true

    const getProgress = () => {
      const section = document.querySelector(".career-section")
      if (!section) return 0

      // scrollTop within the container + viewport offsets
      const scrollTop =
        scroller === window ? window.scrollY : scroller.scrollTop
      const vh =
        scroller === window ? window.innerHeight : scroller.clientHeight

      // section position relative to the scroller's top edge
      const sectionTop = section.getBoundingClientRect().top + scrollTop -
        (scroller === window ? 0 : scroller.getBoundingClientRect().top)

      const startScroll = sectionTop - vh * 0.5
      const endScroll = sectionTop + section.offsetHeight - vh * 0.3
      return Math.min(Math.max((scrollTop - startScroll) / Math.max(endScroll - startScroll, 1), 0), 1)
    }

    const apply = (p) => {
      // 1. Grow the line: maxHeight 0% → 100%
      const tl = document.querySelector(".career-timeline")
      if (tl) {
        tl.style.maxHeight = `${(p * 100).toFixed(4)}%`
        // 2. Fade in: opacity 0 → 1 over first 20% of progress
        tl.style.opacity = Math.min(p / 0.2, 1).toFixed(4)
      }

      // 3. Stagger boxes: opacity 0 → 1, stagger 0.1, duration 0.5
      document.querySelectorAll(".career-info-box").forEach((box, idx) => {
        const st = idx * 0.1
        box.style.opacity = p > st
          ? Math.min((p - st) / 0.5, 1).toFixed(4)
          : "0"
      })

      // 4. Dot pulse: infinite → 1 at progress 0.3
      const dot = document.querySelector(".career-dot")
      if (dot) {
        dot.style.animationIterationCount = p > 0.3 ? "1" : "infinite"
      }

      // 5. Parallax: section y 0 → 20% on desktop
      const section = document.querySelector(".career-section")
      if (section && window.innerWidth > 1024) {
        section.style.transform = `translateY(${(p * 20).toFixed(3)}%)`
      }
    }

    const tick = () => {
      targetP = getProgress()
    }

    const loop = () => {
      if (!alive) return
      currentP += (targetP - currentP) * 0.08
      apply(currentP)
      rafId = requestAnimationFrame(loop)
    }

    // Initial measure after layout is painted
    const init = () => {
      tick()
      loop()
    }

    scroller.addEventListener("scroll", tick, { passive: true })
    window.addEventListener("resize", tick, { passive: true })

    // Delay to let Gatsby finish painting the full page layout
    const t = setTimeout(init, 200)

    return () => {
      alive = false
      cancelAnimationFrame(rafId)
      clearTimeout(t)
      scroller.removeEventListener("scroll", tick)
      window.removeEventListener("resize", tick)
    }
  }, [])

  const getDisplayYear = (period) => {
    if (period.includes("Present")) return "NOW"
    if (period.includes(" - ")) return period.split(" - ")[0]
    return period
  }

  return (
    <div className="career-section" id="experience">
      <div className="career-container">
        <h2>
          My career <span>&amp;</span>
          <br /> experience
        </h2>
        <div className="career-info">
          <div className="career-timeline">
            <div className="career-dot"></div>
          </div>
          {experiences.map((exp, index) => (
            <div key={index} className="career-info-box">
              <div className="career-info-in">
                <div className="career-role">
                  <h4>{exp.position}</h4>
                  <h5>{exp.company}</h5>
                </div>
                <h3>{getDisplayYear(exp.period)}</h3>
              </div>
              <p>{exp.description}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default Experience
